/**
 * Zentrale Datums- und Zeitformatierung basierend auf Server-Konfiguration
 */

import { apiFetch } from './apiConfig';
import React from 'react';
import { createApiUrl } from './apiConfig';
import { getAuthHeaders } from './authUtils';

// Cache für Konfigurationswerte
let formatConfig = {
  dateFormat: 'dd.mm.yyyy', // Standard: deutsches Format
  timeFormat: '24',         // Standard: 24-Stunden-Format
  loaded: false
};

/**
 * Lädt die Formatierungseinstellungen vom Server
 */
export const loadFormatConfig = async () => {
  try {
    // Verwende die neue öffentliche Route, die für alle Benutzer zugänglich ist
    const response = await fetch(createApiUrl('config/format'), {
      headers: getAuthHeaders()
    });
    
    if (response.ok) {
      const config = await response.json();
      formatConfig = {
        dateFormat: config.date_format || 'dd.mm.yyyy',
        timeFormat: config.time_format || '24',
        loaded: true
      };
      console.log('Date/Time format configuration loaded from public endpoint:', formatConfig);
    } else {
      // HTTP-Fehler: Verwende Standardwerte
      console.warn('Could not load format config from public endpoint, using defaults');
      formatConfig = {
        dateFormat: 'dd.mm.yyyy',
        timeFormat: '24', 
        loaded: true
      };
    }
  } catch (error) {
    console.warn('Could not load date/time format configuration, using defaults:', error);
    formatConfig = {
      dateFormat: 'dd.mm.yyyy',
      timeFormat: '24',
      loaded: true
    };
  }
};

/**
 * Wartet bis formatConfig geladen ist (mit Timeout)
 */
export const waitForFormatConfig = async (timeoutMs = 2000) => {
  const startTime = Date.now();
  while (!formatConfig.loaded) {
    if (Date.now() - startTime > timeoutMs) {
      console.warn('⚠️ waitForFormatConfig: Timeout! Verwende Defaults.');
      formatConfig = {
        dateFormat: 'dd.mm.yyyy',
        timeFormat: '24',
        loaded: true
      };
      break;
    }
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  console.log('✅ waitForFormatConfig: Config geladen!', formatConfig);
};

/**
 * Stellt sicher, dass die Konfiguration geladen ist
 */
const ensureConfigLoaded = async () => {
  if (!formatConfig.loaded) {
    await loadFormatConfig();
  }
};

/**
 * Konvertiert date_format string in Locale-Optionen
 */
const getDateLocaleOptions = (dateFormat) => {
  switch (dateFormat) {
    case 'mm/dd/yyyy':
      return {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        locale: 'en-US'
      };
    case 'yyyy-mm-dd':
      return {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        locale: 'sv-SE' // ISO format
      };
    case 'dd.mm.yyyy':
    default:
      return {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        locale: 'de-DE'
      };
  }
};

/**
 * Konvertiert time_format string in Locale-Optionen
 */
const getTimeLocaleOptions = (timeFormat) => {
  const hour12 = timeFormat === '12';
  return {
    hour: '2-digit',
    minute: '2-digit',
    hour12: hour12
  };
};

/**
 * Formatiert ein Datum basierend auf der Konfiguration
 */
export const formatDate = async (dateString) => {
  if (!dateString) return '-';
  
  await ensureConfigLoaded();
  
  const date = new Date(dateString);
  const options = getDateLocaleOptions(formatConfig.dateFormat);
  
  return date.toLocaleDateString(options.locale, {
    day: options.day,
    month: options.month,
    year: options.year
  });
};

/**
 * Formatiert Datum und Zeit basierend auf der Konfiguration
 */
export const formatDateTime = async (dateString) => {
  if (!dateString) return '-';
  
  await ensureConfigLoaded();
  
  const date = new Date(dateString);
  const dateOptions = getDateLocaleOptions(formatConfig.dateFormat);
  const timeOptions = getTimeLocaleOptions(formatConfig.timeFormat);
  
  const formattedDate = date.toLocaleDateString(dateOptions.locale, {
    day: dateOptions.day,
    month: dateOptions.month,
    year: dateOptions.year
  });
  
  const formattedTime = date.toLocaleTimeString(dateOptions.locale, {
    hour: timeOptions.hour,
    minute: timeOptions.minute,
    hour12: timeOptions.hour12
  });
  
  return `${formattedDate} ${formattedTime}`;
};

/**
 * Formatiert ein Geburtsdatum mit Altersangabe
 */
export const formatDateWithAge = async (dateString) => {
  if (!dateString) return '-';
  
  await ensureConfigLoaded();
  
  const birthDate = new Date(dateString);
  const today = new Date();
  
  // Alter berechnen
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  const options = getDateLocaleOptions(formatConfig.dateFormat);
  const formattedDate = birthDate.toLocaleDateString(options.locale, {
    day: options.day,
    month: options.month,
    year: options.year
  });
  
  return `${formattedDate} (${age} Jahre)`;
};

/**
 * Synchrone Versionen für bereits geladene Konfiguration
 * Diese sollten nur verwendet werden, wenn sichergestellt ist, dass die Konfiguration geladen wurde
 */

export const formatDateSync = (dateString) => {
  if (!dateString) return '-';
  if (!formatConfig.loaded) return '-';
  
  const date = new Date(dateString);
  const options = getDateLocaleOptions(formatConfig.dateFormat);
  
  return date.toLocaleDateString(options.locale, {
    day: options.day,
    month: options.month,
    year: options.year
  });
};

export const formatDateTimeSync = (dateString) => {
  if (!dateString) {
      return '-';
  }
  if (!formatConfig.loaded) {
      return '-';
  }
  
  
  const date = new Date(dateString);
  const dateOptions = getDateLocaleOptions(formatConfig.dateFormat);
  const timeOptions = getTimeLocaleOptions(formatConfig.timeFormat);
  
  const formattedDate = date.toLocaleDateString(dateOptions.locale, {
    day: dateOptions.day,
    month: dateOptions.month,
    year: dateOptions.year
  });
  
  const formattedTime = date.toLocaleTimeString(dateOptions.locale, {
    hour: timeOptions.hour,
    minute: timeOptions.minute,
    hour12: timeOptions.hour12
  });
  
  const result = `${formattedDate} ${formattedTime}`;
  return result;
};

export const formatDateWithAgeSync = (dateString) => {
  if (!dateString) {
      return '-';
  }
  if (!formatConfig.loaded) {
      return '-';
  }
  
  
  const birthDate = new Date(dateString);
  const today = new Date();
  
  // Alter berechnen
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  const options = getDateLocaleOptions(formatConfig.dateFormat);
  const formattedDate = birthDate.toLocaleDateString(options.locale, {
    day: options.day,
    month: options.month,
    year: options.year
  });
  
  const result = `${formattedDate} (${age} Jahre)`;
  return result;
};

/**
 * Gibt die aktuelle Formatierungskonfiguration zurück
 */
export const getFormatConfig = () => formatConfig;

/**
 * Formatiert Zeit im hh:mm Format (immer 24h)
 */
export const formatTimeSync = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Hook für React-Komponenten zum Laden der Konfiguration
 */
export const useFormatConfig = () => {
  const [isLoaded, setIsLoaded] = React.useState(formatConfig.loaded);
  
  React.useEffect(() => {
    const loadConfig = async () => {
      if (!formatConfig.loaded) {
        await loadFormatConfig();
        setIsLoaded(true);
      }
    };
    
    loadConfig();
  }, []);
  
  return { isLoaded, formatConfig };
}; 